import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { Box, CircularProgress, Typography } from '@mui/joy';
import { AUTH_TOKEN_NAME } from '../constants';
import { RESOLVE_INTERVIEW_TOKEN } from '../gql/interview.resolveToken.mutation';
import { parseGraphQLError } from '../utils';

/**
 * Handles the magic link for video interview invitations.
 * When a candidate clicks the magic link in their email, this component:
 * 1. Extracts the token from the URL
 * 2. Calls the resolveInterviewToken mutation to validate the token
 * 3. Stores the session token in localStorage
 * 4. Redirects the candidate to the video interview page
 */
const InterviewMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [resolveInterviewToken] = useMutation(RESOLVE_INTERVIEW_TOKEN);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setError('No token provided. Please use the link from your email.');
      return;
    }

    const resolveToken = async () => {
      try {
        const { data } = await resolveInterviewToken({
          variables: { token }
        });

        const { user, application } = data.resolveInterviewToken;

        if (!user?.token) {
          setError('Invalid token response. Please contact support.');
          return;
        }

        // Store the session token for authenticated requests
        localStorage.setItem(AUTH_TOKEN_NAME, user.token);

        // Redirect to the video interview page
        const clientId = application.client._id;
        const jobId = application.job._id;
        
        navigate(`/company/${clientId}/job/${jobId}/video-interview-intro`, { replace: true });
      } catch (err) {
        console.error('Failed to resolve interview token:', err);
        setError(parseGraphQLError(err) || 'Failed to process your invitation. The link may have expired.');
      }
    };

    resolveToken();
  }, [searchParams, resolveInterviewToken, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        p: 3,
      }}
    >
      {error ? (
        <>
          <Typography level="h4" color="danger">
            Unable to Process Invitation
          </Typography>
          <Typography level="body-md" textAlign="center">
            {error}
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size="lg" />
          <Typography level="body-md">
            Processing your interview invitation...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default InterviewMagicLink;
